import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';

const VirtualBackgroundContext = createContext();

// Initial values structure for each card
const initialBackgroundCardValues = {
  virtualBackgroundContextCardName: '',
  virtualBackgroundContextCompany: '',
  virtualBackgroundContextJobTitle: '',
  virtualBackgroundContextLocation: '',
  virtualBackgroundContextQRCodeColor: '#000000', // Default QR code color
  virtualBackgroundContextQRCodeLogoUrl: null,
  virtualBackgroundContextShowQRCode: true, // Show QR code by default
  virtualBackgroundContextCondensedView: false, // Condensed view by default
  virtualBackgroundContextBackgroundImageUrl: '', // URL for the background image
  virtualBackgroundContextShowCardName: true, // Show card name by default
  virtualBackgroundContextShowCompany: false, // Show company by default
  virtualBackgroundContextShowJobTitle: false, // Show job title by default
  virtualBackgroundContextShowLocation: false, // Show location by default
};

export const VirtualBackgroundProvider = ({ children }) => {
  const { authState } = useAuth();
  const { addError } = useError();
  const [backgroundCardState, setBackgroundCardState] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Validation functions
  const validateCardId = useCallback((cardId) => {
    if (!cardId || typeof cardId !== 'string' || cardId.trim() === '') {
      throw new Error('Invalid card ID provided');
    }
    return cardId.trim();
  }, []);

  const validateStringValue = useCallback((value, fieldName) => {
    if (value !== null && value !== undefined && typeof value !== 'string') {
      throw new Error(`${fieldName} must be a string`);
    }
    return value;
  }, []);

  const validateBooleanValue = useCallback((value, fieldName) => {
    if (typeof value !== 'boolean') {
      throw new Error(`${fieldName} must be a boolean`);
    }
    return value;
  }, []);

  const validateColorValue = useCallback((color) => {
    if (!color || typeof color !== 'string') {
      throw new Error('Color must be a valid string');
    }
    // Basic hex color validation
    if (!/^#[0-9A-F]{6}$/i.test(color)) {
      throw new Error('Color must be a valid hex color (e.g., #000000)');
    }
    return color;
  }, []);

  const validateImageUrl = useCallback((url) => {
    if (url && typeof url !== 'string') {
      throw new Error('Image URL must be a string');
    }
    if (!url || url.trim() === '') {
      return url;
    }
    const normalized = url.trim();
    const isHttpUrl = /^https?:\/\/.+/i.test(normalized);
    const isBlobUrl = /^blob:/i.test(normalized);
    const isDataImageUrl = /^data:image\/[a-zA-Z]+;base64,/i.test(normalized);
    if (!isHttpUrl && !isBlobUrl && !isDataImageUrl) {
      throw new Error('Image URL must be a valid HTTP/HTTPS URL');
    }
    return url;
  }, []);

  // Function to update background card data with validation
  const updateBackgroundCardData = useCallback((cardId, updates) => {
    try {
      const validCardId = validateCardId(cardId);

      setBackgroundCardState(prevState => ({
        ...prevState,
        [validCardId]: {
          ...prevState[validCardId],
          ...updates,
        },
      }));

      // TODO: Implement backend sync when available
      // try {
      //   await apiClient.put(`/api/virtual-backgrounds/${validCardId}`, updates);
      //   // Success handled by API response
      // } catch (error) {
      //   console.warn('Failed to sync with server. Changes saved locally.');
      // }

    } catch (error) {
      console.error('[VirtualBackgroundContext] Error updating background card data:', error);
      // Only show user-relevant errors, log technical errors
      if (error.message?.includes('validation') || error.message?.includes('permission')) {
        addError(error.message || 'Failed to update virtual background');
      }
    }
  }, [validateCardId, addError]);

  // Function to initialize a card with default values if it doesn't exist
  const initializeBackgroundCard = useCallback((cardId) => {
    try {
      const validCardId = validateCardId(cardId);

      if (!backgroundCardState[validCardId]) {
        setBackgroundCardState(prevState => ({
          ...prevState,
          [validCardId]: initialBackgroundCardValues,
        }));
        console.log(`[VirtualBackgroundContext] Initialized card ${validCardId} with default values`);
      }
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error initializing background card:', error);
      // Only show user-relevant errors, log technical errors
      if (error.message?.includes('validation') || error.message?.includes('permission')) {
        addError(error.message || 'Failed to initialize virtual background');
      }
    }
  }, [backgroundCardState, validateCardId, addError]);

  // Load virtual background data from backend
  const loadVirtualBackgroundData = useCallback(async (cardId) => {
    try {
      if (!cardId) {
        console.warn('[VirtualBackgroundContext] Cannot load data: No card ID provided');
        return;
      }

      const validCardId = validateCardId(cardId);
      setIsLoading(true);

      // TODO: Implement backend API call when available
      // Example:
      // const response = await apiClient.get(`/api/virtual-backgrounds/${validCardId}`);
      // const serverData = response.data;
      // 
      // setBackgroundCardState(prevState => ({
      //   ...prevState,
      //   [validCardId]: {
      //     ...initialBackgroundCardValues,
      //     ...serverData
      //   }
      // }));

      console.log(`[VirtualBackgroundContext] Loaded virtual background data for card ${validCardId}`);
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error loading virtual background data:', error);
      // Only show user-relevant errors, log technical errors
      if (error.message?.includes('validation') || error.message?.includes('permission')) {
        addError('Failed to load virtual background data. Using default values.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [validateCardId, addError]);

  // Clear virtual background data for a card
  const clearVirtualBackgroundData = useCallback((cardId) => {
    try {
      const validCardId = validateCardId(cardId);

      setBackgroundCardState(prevState => {
        const newState = { ...prevState };
        delete newState[validCardId];
        return newState;
      });

      console.log(`[VirtualBackgroundContext] Cleared virtual background data for card ${validCardId}`);
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error clearing virtual background data:', error);
      // Only show user-relevant errors, log technical errors
      if (error.message?.includes('validation') || error.message?.includes('permission')) {
        addError('Failed to clear virtual background data');
      }
    }
  }, [validateCardId, addError]);

  // Setter functions for various attributes with validation
  const setVirtualBackgroundContextCardName = useCallback((cardId, name) => {
    try {
      const validCardId = validateCardId(cardId);
      const validName = validateStringValue(name, 'Card name');
      updateBackgroundCardData(validCardId, { virtualBackgroundContextCardName: validName });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting card name:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set card name');
      }
    }
  }, [validateCardId, validateStringValue, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextCompany = useCallback((cardId, company) => {
    try {
      const validCardId = validateCardId(cardId);
      const validCompany = validateStringValue(company, 'Company');
      updateBackgroundCardData(validCardId, { virtualBackgroundContextCompany: validCompany });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting company:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set company');
      }
    }
  }, [validateCardId, validateStringValue, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextQRCodeLogoUrl = useCallback((cardId, logoUrl) => {
    try {
      const validCardId = validateCardId(cardId);
      const validLogoUrl = validateImageUrl(logoUrl);
      updateBackgroundCardData(validCardId, { virtualBackgroundContextQRCodeLogoUrl: validLogoUrl });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting QR code logo URL:', error);
      addError(error.message || 'Failed to set QR code logo');
    }
  }, [validateCardId, validateImageUrl, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextJobTitle = useCallback((cardId, jobTitle) => {
    try {
      const validCardId = validateCardId(cardId);
      const validJobTitle = validateStringValue(jobTitle, 'Job title');
      updateBackgroundCardData(validCardId, { virtualBackgroundContextJobTitle: validJobTitle });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting job title:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set job title');
      }
    }
  }, [validateCardId, validateStringValue, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextLocation = useCallback((cardId, location) => {
    try {
      const validCardId = validateCardId(cardId);
      const validLocation = validateStringValue(location, 'Location');
      updateBackgroundCardData(validCardId, { virtualBackgroundContextLocation: validLocation });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting location:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set location');
      }
    }
  }, [validateCardId, validateStringValue, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextQRCodeColor = useCallback((cardId, color) => {
    try {
      const validCardId = validateCardId(cardId);
      const validColor = validateColorValue(color);
      updateBackgroundCardData(validCardId, { virtualBackgroundContextQRCodeColor: validColor });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting QR code color:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set QR code color');
      }
    }
  }, [validateCardId, validateColorValue, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextShowQRCode = useCallback((cardId, show) => {
    try {
      const validCardId = validateCardId(cardId);
      const validShow = validateBooleanValue(show, 'Show QR code');
      updateBackgroundCardData(validCardId, { virtualBackgroundContextShowQRCode: validShow });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting show QR code:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set QR code visibility');
      }
    }
  }, [validateCardId, validateBooleanValue, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextCondensedView = useCallback((cardId, condensed) => {
    try {
      const validCardId = validateCardId(cardId);
      const validCondensed = validateBooleanValue(condensed, 'Condensed view');
      updateBackgroundCardData(validCardId, { virtualBackgroundContextCondensedView: validCondensed });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting condensed view:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set condensed view');
      }
    }
  }, [validateCardId, validateBooleanValue, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextBackgroundImageUrl = useCallback((cardId, imageUrl) => {
    try {
      const validCardId = validateCardId(cardId);
      const validImageUrl = validateImageUrl(imageUrl);
      updateBackgroundCardData(validCardId, { virtualBackgroundContextBackgroundImageUrl: validImageUrl });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting background image URL:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set background image');
      }
    }
  }, [validateCardId, validateImageUrl, updateBackgroundCardData, addError]);

  // Setter functions for show/hide toggles
  const setVirtualBackgroundContextShowCardName = useCallback((cardId, show) => {
    try {
      const validCardId = validateCardId(cardId);
      const validShow = validateBooleanValue(show, 'Show card name');
      updateBackgroundCardData(validCardId, { virtualBackgroundContextShowCardName: validShow });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting show card name:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set card name visibility');
      }
    }
  }, [validateCardId, validateBooleanValue, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextShowCompany = useCallback((cardId, show) => {
    try {
      const validCardId = validateCardId(cardId);
      const validShow = validateBooleanValue(show, 'Show company');
      updateBackgroundCardData(validCardId, { virtualBackgroundContextShowCompany: validShow });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting show company:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set company visibility');
      }
    }
  }, [validateCardId, validateBooleanValue, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextShowJobTitle = useCallback((cardId, show) => {
    try {
      const validCardId = validateCardId(cardId);
      const validShow = validateBooleanValue(show, 'Show job title');
      updateBackgroundCardData(validCardId, { virtualBackgroundContextShowJobTitle: validShow });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting show job title:', error);
      // Only show validation errors to users
      if (error.message?.includes('validation') || error.message?.includes('must be')) {
        addError(error.message || 'Failed to set job title visibility');
      }
    }
  }, [validateCardId, validateBooleanValue, updateBackgroundCardData, addError]);

  const setVirtualBackgroundContextShowLocation = useCallback((cardId, show) => {
    try {
      const validCardId = validateCardId(cardId);
      const validShow = validateBooleanValue(show, 'Show location');
      updateBackgroundCardData(validCardId, { virtualBackgroundContextShowLocation: validShow });
    } catch (error) {
      console.error('[VirtualBackgroundContext] Error setting show location:', error);
      addError(error.message || 'Failed to set location visibility');
    }
  }, [validateCardId, validateBooleanValue, updateBackgroundCardData, addError]);

  return (
    <VirtualBackgroundContext.Provider
      value={{
        backgroundCardState,
        isLoading,
        setVirtualBackgroundContextCardName,
        setVirtualBackgroundContextCompany,
        setVirtualBackgroundContextJobTitle,
        setVirtualBackgroundContextLocation,
        setVirtualBackgroundContextQRCodeColor,
        setVirtualBackgroundContextShowQRCode,
        setVirtualBackgroundContextCondensedView,
        setVirtualBackgroundContextBackgroundImageUrl,
        setVirtualBackgroundContextShowCardName,
        setVirtualBackgroundContextShowCompany,
        setVirtualBackgroundContextShowJobTitle,
        setVirtualBackgroundContextShowLocation,
        setVirtualBackgroundContextQRCodeLogoUrl,
        initializeBackgroundCard,
        loadVirtualBackgroundData,
        clearVirtualBackgroundData
      }}
    >
      {children}
    </VirtualBackgroundContext.Provider>
  );
};

// Custom hook to use the VirtualBackgroundContext
export const useVirtualBackgroundContext = () => {
  const context = useContext(VirtualBackgroundContext);
  if (!context) {
    throw new Error('useVirtualBackgroundContext must be used within a VirtualBackgroundProvider');
  }
  return context;
};

// Custom hook to initialize card based on cardId
export const useInitializeCard = (cardId) => {
  const { initializeBackgroundCard, loadVirtualBackgroundData } = useVirtualBackgroundContext();

  useEffect(() => {
    if (cardId) {
      try {
        console.log('Initializing card with ID context file:', cardId);
        initializeBackgroundCard(cardId);
        loadVirtualBackgroundData(cardId);
      } catch (error) {
        console.error('[useInitializeCard] Error initializing card:', error);
      }
    }
  }, [cardId, initializeBackgroundCard, loadVirtualBackgroundData]);
};


